<template>
  <div v-for="id in slotIndexes" :key="`std-date-${id}`" class="row mt-4">
    <label
      class="form-label"
      :class="{ required: id === 0 }"
      :for="`dateRequestDate${id + 1}`"
      >{{
        t(`subscribeToEventUpdatesModal.firstStep.form.date${id + 1}.label`)
      }}</label
    >
    <div class="mt-1 relative w-full hidden sm:block">
      <Datepicker
        v-model="dates[id]"
        model-type="yyyy-MM-dd'T'HH:mm"
        time-picker-inline
        :format-locale="deLocale"
        format="dd.MM.yyyy, HH:mm"
        locale="de"
        :select-text="t('dateRequest.instantBooking.datePickerSelect')"
        text-input
        minutes-increment="30"
        minutes-grid-increment="30"
        :start-time="startTime"
        :disabled-dates="isDateDisabled"
        :disabled-week-days="disabledWeekDays"
        :start-date="startDate"
        :placeholder="t('product.show.checkout.selectDate')"
        @open="emit('clear-errors', 'dates')"
      />
    </div>
    <div class="mt-1 relative w-full sm:hidden">
      <div class="relative w-full">
        <input
          :id="`dateRequestDate${id + 1}`"
          v-model="dates[id]"
          :min="minDateFormatted"
          step="1800"
          :placeholder="
            t(
              `subscribeToEventUpdatesModal.firstStep.form.date${id + 1}.placeholder`
            )
          "
          type="datetime-local"
          :autocomplete="`dateRequestDate${id + 1}`"
          class="box-border min-w-0 w-full text-left rounded-lg border border-gray-200 bg-white ![padding-block-start:0.25rem] ![padding-block-end:0.25rem] ![padding-inline-start:0] ![padding-inline-end:0]"
          :data-test-id="`date-request-date-picker-${id + 1}`"
          :name="`dateRequestDate${id + 1}`"
          @input="emit('clear-errors', 'dates')"
        />
      </div>
      <span
        v-if="isSlotOnDisabledWeekday(id)"
        class="mt-1 block text-red text-sm"
        :data-test-id="`date-request-native-weekday-invalid-${id}`"
      >
        {{ t('dateRequest.nativeDatePicker.disabledWeekday') }}
      </span>
    </div>

    <span
      v-if="getDateSlotError(id)"
      data-test-id="date-request-form-invalid"
      class="text-red text-sm"
    >
      {{ getDateSlotError(id) }}
    </span>

    <div
      v-if="isSlotAvailable(dates[id] ?? '') && !isSlotOnDisabledWeekday(id)"
      class="mt-2 text-sm text-teal-600 font-medium"
    >
      {{ t('dateRequest.availableDateHelperText') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { de as deLocale } from 'date-fns/locale'
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import { isDateOnDisabledWeekday } from '~/utils/dateHelpers'

interface Props {
  minDateFormatted: string
  startTime: { hours: number; minutes: number }
  startDate: Date
  disabledWeekDays: number[]
  isDateDisabled: (date: Date) => boolean
  isSlotAvailable: (userDate: string) => boolean
  errors?: DateRequestErrors | null
  invalidKeys?: DateRequestInvalidKeys
}

const {
  minDateFormatted,
  startTime,
  startDate,
  disabledWeekDays,
  isDateDisabled,
  isSlotAvailable,
  errors = {},
  invalidKeys: _invalidKeys = [],
} = defineProps<Props>()

const dates = defineModel<string[]>('dates', { required: true })

const emit = defineEmits<{
  (e: 'clear-errors', field: string): void
}>()

const { t } = useI18n()

const slotIndexes = [0, 1, 2]

const getDateSlotError = (index: number) =>
  errors?.dateSlots?.[index] ?? (index === 0 ? errors?.dates : undefined)

const isSlotOnDisabledWeekday = (index: number) =>
  isDateOnDisabledWeekday(dates.value[index] ?? '', disabledWeekDays)
</script>

<style lang="postcss" scoped>
.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}

.dp__menu {
  font-family: 'Lexend';
}
:deep(.dp__input) {
  @apply rounded-lg;
}
</style>
